import React, { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Price } from "@/components/common";
import { Copy, Check, Upload, Building2, PartyPopper, Landmark, Truck } from "lucide-react";

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="font-mono-num font-semibold truncate">{value}</div>
      </div>
      <Button variant="ghost" size="icon" onClick={copy} data-testid="bank-copy-button">
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function SignupDialog({ project, open, onOpenChange }) {
  const { t, lang, pick } = useI18n();
  const { user, refreshUser } = useAuth();
  const { settings } = useSettings();
  const bank = settings?.bank || {};

  const [step, setStep] = useState(1);
  const [qty, setQty] = useState(1);
  const [usePoints, setUsePoints] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pname, setPname] = useState(user?.name || "");
  const [pphone, setPphone] = useState(user?.phone || "");
  const [note, setNote] = useState("");
  const isShop = project.category === "shop";
  const [shipName, setShipName] = useState(user?.name || "");
  const [shipPhone, setShipPhone] = useState(user?.phone || "");
  const [shipAddress, setShipAddress] = useState("");
  const [order, setOrder] = useState(null);
  const [receipt, setReceipt] = useState("");
  const [bankRef, setBankRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [voucherCode, setVoucherCode] = useState("");

  const price = Number(project.price) || 0;
  const total = useMemo(() => price * qty, [price, qty]);
  const balance = Number(user?.points || 0);
  const maxRedeem = Math.min(balance, total);
  const redeem = usePoints ? Math.min(Number(pointsToUse) || 0, maxRedeem) : 0;
  const cashAfterPoints = Math.max(total - redeem, 0);
  const selVoucher = vouchers.find((v) => v.code === voucherCode);
  const voucherDiscount = selVoucher ? Math.min(Number(selVoucher.value) || 0, cashAfterPoints) : 0;
  const cash = Math.max(cashAfterPoints - voucherDiscount, 0);
  const isFree = total === 0;

  useEffect(() => {
    if (open && user) {
      api.get("/me/vouchers").then((r) => setVouchers((r.data.vouchers || []).filter((v) => v.status === "active"))).catch(() => {});
    }
  }, [open, user]);

  const title = pick(project, "title") || project.title_zh;

  const createOrder = async () => {
    if (isShop && !shipAddress.trim()) {
      toast.error(lang === "zh" ? "请填写收货地址" : "Please enter a shipping address");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/orders", {
        project_id: project.id,
        quantity: qty,
        points_used: redeem,
        participant_name: pname,
        participant_phone: pphone,
        note,
        shipping_name: shipName,
        shipping_phone: shipPhone,
        shipping_address: shipAddress,
        voucher_code: voucherCode,
      });
      setOrder(res.data);
      await refreshUser();
      if (isFree || Number(res.data.cash_amount) === 0) {
        setStep(4);
      } else {
        setStep(2);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error(lang === "zh" ? "图片请小于 5MB" : "Image must be under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setReceipt(reader.result);
    reader.readAsDataURL(file);
  };

  const submitReceipt = async () => {
    if (!receipt) {
      toast.error(lang === "zh" ? "请先上传汇款收据" : "Please upload the receipt");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/receipt`, { receipt_data: receipt, bank_reference: bankRef });
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const steps = [t("bankin.step1"), t("bankin.step2"), t("bankin.step3"), t("bankin.step4")];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-card max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{t("bankin.title")}</DialogTitle>
          <DialogDescription className="text-sm">{title}</DialogDescription>
        </DialogHeader>

        {/* stepper */}
        <div className="flex items-center gap-1 mb-2">
          {steps.map((s, i) => (
            <div key={i} className="flex-1">
              <div className={`h-1.5 rounded-full ${i + 1 <= step ? "bg-[hsl(var(--brand-gold))]" : "bg-border"}`} />
              <div className={`mt-1 text-[10px] text-center ${i + 1 <= step ? "text-[hsl(var(--brand-bronze))] font-semibold" : "text-muted-foreground"}`}>{s}</div>
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4" data-testid="signup-step-1">
            {!isFree && (
              <div className="flex items-center justify-between">
                <Label>{t("common.quantity")}</Label>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))}>-</Button>
                  <span className="w-8 text-center font-semibold">{qty}</span>
                  <Button variant="outline" size="icon" onClick={() => setQty((q) => q + 1)}>+</Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <div>
                <Label htmlFor="pname">{t("auth.name")}</Label>
                <Input id="pname" data-testid="signup-name-input" value={pname} onChange={(e) => setPname(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="pphone">{t("auth.phone")}</Label>
                <Input id="pphone" data-testid="signup-phone-input" value={pphone} onChange={(e) => setPphone(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="pnote">{lang === "zh" ? "备注（选填）" : "Note (optional)"}</Label>
                <Textarea id="pnote" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
              </div>
            </div>

            {isShop && (
              <div className="rounded-lg border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold-soft))]/25 p-3 space-y-2" data-testid="shop-shipping-section">
                <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))] font-semibold text-sm">
                  <Truck className="h-4 w-4" /> {t("shop.shippingTitle")}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="shipname" className="text-xs">{t("shop.shippingName")}</Label>
                    <Input id="shipname" data-testid="shipping-name-input" value={shipName} onChange={(e) => setShipName(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="shipphone" className="text-xs">{t("shop.shippingPhone")}</Label>
                    <Input id="shipphone" data-testid="shipping-phone-input" value={shipPhone} onChange={(e) => setShipPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="shipaddr" className="text-xs">{t("shop.shippingAddress")} <span className="text-[hsl(var(--brand-vermillion))]">*</span></Label>
                  <Textarea id="shipaddr" data-testid="shipping-address-input" rows={2} value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder={t("shop.addressPlaceholder")} />
                </div>
              </div>
            )}

            {!isFree && balance > 0 && (
              <div className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between">
                  <Label className="cursor-pointer">{t("bankin.usePoints")}</Label>
                  <Switch checked={usePoints} onCheckedChange={(v) => { setUsePoints(v); setPointsToUse(v ? maxRedeem : 0); }} data-testid="use-points-switch" />
                </div>
                {usePoints && (
                  <div className="mt-2">
                    <div className="text-xs text-muted-foreground mb-1">{t("bankin.pointsAvail")}: {balance.toFixed(2)} · (1{t("common.pointsUnit")}=RM1)</div>
                    <Input type="number" min={0} max={maxRedeem} value={pointsToUse}
                      onChange={(e) => setPointsToUse(Math.min(Number(e.target.value), maxRedeem))}
                      data-testid="points-input" />
                  </div>
                )}
              </div>
            )}

            {vouchers.length > 0 && cashAfterPoints > 0 && (
              <div className="rounded-lg border border-[hsl(var(--brand-gold))] p-3" data-testid="voucher-select">
                <div className="text-xs font-medium text-[hsl(var(--brand-bronze))] mb-1.5">{lang === "zh" ? "升级礼券" : "Level-up voucher"}</div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setVoucherCode("")}
                    className={`text-xs rounded-md px-2.5 py-1.5 border ${!voucherCode ? "bg-[hsl(var(--brand-gold)/0.2)] border-[hsl(var(--brand-gold))] font-semibold" : "border-border text-muted-foreground"}`}
                    data-testid="voucher-none">{lang === "zh" ? "不使用" : "None"}</button>
                  {vouchers.map((v) => (
                    <button key={v.code} type="button" onClick={() => setVoucherCode(v.code)}
                      className={`text-xs rounded-md px-2.5 py-1.5 border ${voucherCode === v.code ? "bg-[hsl(var(--brand-gold)/0.2)] border-[hsl(var(--brand-gold))] font-semibold" : "border-border"}`}
                      data-testid={`voucher-opt-${v.code}`}>RM{Number(v.value).toFixed(0)} {lang === "zh" ? "折扣" : "off"}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-lg bg-[hsl(var(--secondary))] p-3 space-y-1">
              <div className="flex justify-between text-sm"><span>{t("common.total")}</span><Price value={total} /></div>
              {redeem > 0 && <div className="flex justify-between text-sm text-[hsl(var(--brand-vermillion))]"><span>{t("bankin.usePoints")}</span><span>-RM{redeem.toFixed(2)}</span></div>}
              {voucherDiscount > 0 && <div className="flex justify-between text-sm text-[hsl(var(--brand-vermillion))]" data-testid="voucher-discount-row"><span>{lang === "zh" ? "礼券折扣" : "Voucher"}</span><span>-RM{voucherDiscount.toFixed(2)}</span></div>}
              <div className="flex justify-between font-semibold"><span>{t("bankin.cashToPay")}</span><Price value={cash} className="text-[hsl(var(--brand-bronze))]" /></div>
            </div>

            <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy || !pname} onClick={createOrder} data-testid="signup-confirm-button">
              {isFree || cash === 0 ? t("common.signup") : t("bankin.createOrder")}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="signup-step-2">
            <div className="flex items-center gap-2 text-[hsl(var(--brand-bronze))]"><Landmark className="h-5 w-5" /><span className="font-semibold">{t("bankin.bankInfo")}</span></div>
            <div className="rounded-lg border border-border p-3">
              <CopyRow label={t("bankin.bankName")} value={bank.bank_name || "-"} />
              <CopyRow label={t("bankin.accountName")} value={bank.account_name || "-"} />
              <CopyRow label={t("bankin.accountNo")} value={bank.account_no || "-"} />
              <CopyRow label={t("bankin.cashToPay")} value={`RM${Number(order?.cash_amount || cash).toFixed(2)}`} />
            </div>
            <p className="text-xs text-muted-foreground">{lang === "zh" ? bank.note_zh : bank.note_en}</p>
            {bank.qr_image && (
              <div className="flex flex-col items-center gap-1 rounded-lg border border-border p-3" data-testid="bankin-qr">
                <span className="text-xs font-medium text-[hsl(var(--brand-bronze))]">{lang === "zh" ? "扫码付款" : "Scan to pay"}</span>
                <img src={bank.qr_image} alt="payment qr" className="h-44 w-44 rounded-md object-contain bg-white" />
              </div>
            )}
            <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" onClick={() => setStep(3)} data-testid="goto-receipt-button">
              {t("bankin.step3")}
            </Button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="signup-step-3">
            <Label className="flex items-center gap-2"><Upload className="h-4 w-4" /> {t("bankin.uploadReceipt")}</Label>
            <input type="file" accept="image/*" onChange={onFile} data-testid="bank-in-upload-receipt-input"
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[hsl(var(--brand-gold-soft))] file:px-4 file:py-2 file:font-semibold" />
            {receipt && <img src={receipt} alt="receipt" className="max-h-52 rounded-lg border border-border object-contain" />}
            <div>
              <Label htmlFor="bankref">{t("bankin.bankRef")}</Label>
              <Input id="bankref" value={bankRef} onChange={(e) => setBankRef(e.target.value)} data-testid="bank-ref-input" />
            </div>
            <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy} onClick={submitReceipt} data-testid="submit-receipt-button">
              {t("bankin.submitReceipt")}
            </Button>
          </div>
        )}

        {step === 4 && (
          <div className="text-center py-6 space-y-3" data-testid="signup-step-4">
            <div className="mx-auto h-14 w-14 rounded-full bg-[hsl(var(--brand-jade-mist))] flex items-center justify-center">
              <PartyPopper className="h-7 w-7 text-[hsl(var(--brand-bronze))]" />
            </div>
            <h3 className="font-display text-xl font-semibold">{isFree ? (lang === "zh" ? "报名成功" : "Registered!") : t("bankin.pendingTitle")}</h3>
            <p className="text-sm text-muted-foreground px-2">{isFree ? (lang === "zh" ? "感恩参与！你的报名已成功记录，可在个人页面查看。" : "Thank you for joining! Your registration is confirmed and visible on your profile.") : t("bankin.pendingDesc")}</p>
            <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" onClick={() => onOpenChange(false)} data-testid="signup-done-button">
              {t("common.close")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
